import {
  formatTimeAgo,
  getFullImageUrl,
  generateAvatarUrl,
  canEditComment,
} from '../utils/communityHelpers';

/**
 * Comment Adapter
 * Maps API comment responses to UI-expected format
 * Handles nested reply structures and user data transformation
 */

/**
 * Map API comment response to UI format
 * Recursively handles nested replies (up to 5 levels deep)
 * @param {Object} apiComment - Comment from API
 * @returns {Object} - Transformed comment for UI
 */
export const mapCommentFromAPI = (apiComment) => {
  try {
    if (!apiComment) return null;

    // Extract user information safely
    const user = apiComment.user || {};
    const userName = user.full_name || user.first_name + ' ' + user.last_name || 'Anonymous';
    const userImage = user.profile_photo_path;

    // Check if comment can still be edited (within 5 minutes)
    const isEditable = canEditComment(apiComment.created_at);

    return {
      // Core fields
      id: apiComment.id,
      author: userName.trim(),
      // authorImage: userImage ? getFullImageUrl(userImage) : generateAvatarUrl(userName),
      authorImage: userImage ? userImage : userName,

      content: apiComment.content || '',
      image: (() => {
        const imageValue = apiComment.image_url || apiComment.image_path;
        if (!imageValue) return null;
        
        // If it's a blob URL, return as-is (for optimistic updates)
        if (imageValue.startsWith('blob:')) {
          return imageValue;
        }
        
        // If it already contains the full path with base URL, return as-is
        if (imageValue.includes('/uploads/comment_image/')) {
          // Check if it already has the protocol (http/https)
          if (imageValue.startsWith('http')) {
            return imageValue;
          }
          // If it starts with /uploads, prepend only the base URL
          if (imageValue.startsWith('/uploads')) {
            return `${process.env.REACT_APP_API_BASE_URL}${imageValue}`;
          }
          // If it already has the path but no protocol, it might be malformed
          return imageValue;
        }
        
        // Otherwise, it's just a filename - construct full URL
        return `${process.env.REACT_APP_API_BASE_URL}/uploads/comment_image/${imageValue}`;
      })(),
      time: formatTimeAgo(apiComment.created_at),

      // Link preview data
      link_preview: apiComment.link_preview || null,

      // Stats - HYBRID APPROACH: Combine legacy likes (from mobile) + new reactions (from web)
      // This ensures cross-platform compatibility
      likes: parseInt((apiComment.total_reactions || 0) + (apiComment.likes_count || 0)) || 0,
      liked: Boolean(apiComment.user_reaction || apiComment.isLiked),

      // Emoji reactions - Parse actual reaction data from backend
      userReaction: apiComment.user_reaction || (apiComment.isLiked ? 'like' : null),
      reactions: (() => {
        try {
          // Parse reactions from backend (can be JSON string or object)
          let reactionsData = apiComment.reactions;
          if (typeof reactionsData === 'string') {
            reactionsData = JSON.parse(reactionsData);
          }
          return {
            like: parseInt(reactionsData?.like || 0) || 0,
            love: parseInt(reactionsData?.love || 0) || 0,
            haha: parseInt(reactionsData?.haha || 0) || 0,
            wow: parseInt(reactionsData?.wow || 0) || 0,
            sad: parseInt(reactionsData?.sad || 0) || 0,
            angry: parseInt(reactionsData?.angry || 0) || 0,
          };
        } catch (error) {
          console.error('Error parsing comment reactions:', error, apiComment.reactions);
          // Fallback to likes_count if reactions parsing fails
          return {
            like: apiComment.likes_count || 0,
            love: 0,
            haha: 0,
            wow: 0,
            sad: 0,
            angry: 0,
          };
        }
      })(),

      // Nested replies - Recursively map all replies
      replies: apiComment.replies && Array.isArray(apiComment.replies)
        ? apiComment.replies.map(mapCommentFromAPI).filter(Boolean)
        : [],

      // Parent reference for replies
      parentId: apiComment.parent_id || null,

      // Edit state
      isEditable: isEditable,
      isEditing: false, // UI state, initially false

      // Additional metadata
      _metadata: {
        postId: apiComment.post_id,
        userId: apiComment.user_id,
        createdAt: apiComment.created_at,
        updatedAt: apiComment.updated_at,
      },
    };
  } catch (error) {
    console.error('Error mapping comment from API:', error, apiComment);
    return null;
  }
};

/**
 * Map array of API comments to UI format
 * @param {Array} apiComments - Comments array from API
 * @returns {Array} - Transformed comments for UI
 */
export const mapCommentsFromAPI = (apiComments) => {
  try {
    if (!Array.isArray(apiComments)) {
      console.warn('mapCommentsFromAPI: Expected array, got:', typeof apiComments);
      return [];
    }

    return apiComments.map(mapCommentFromAPI).filter(Boolean);
  } catch (error) {
    console.error('Error mapping comments from API:', error);
    return [];
  }
};

/**
 * Map comment like/unlike API response
 * Backend returns: { success: true, data: { like_count: number, liked: boolean } }
 * @param {Object} apiResponse - Response from like/unlike endpoint
 * @returns {Object} - Updated like data
 */
export const mapCommentLikeResponseFromAPI = (apiResponse) => {
  try {
    // Handle multiple possible response structures
    let likeData = null;

    // Structure 1: { data: { data: { like_count, liked } } }
    if (apiResponse?.data?.data?.like_count !== undefined) {
      likeData = apiResponse.data.data;
    }
    // Structure 2: { data: { like_count, liked } }
    else if (apiResponse?.data?.like_count !== undefined) {
      likeData = apiResponse.data;
    }
    // Structure 3: Direct { like_count, liked }
    else if (apiResponse?.like_count !== undefined) {
      likeData = apiResponse;
    }

    if (!likeData) {
      console.warn('Could not find like data in response:', apiResponse);
      return null;
    }

    return {
      likeCount: likeData.like_count || 0,
      liked: likeData.liked ?? false,
    };
  } catch (error) {
    console.error('Error mapping comment like response from API:', error, apiResponse);
    return null;
  }
};

/**
 * Update comment object with new like data
 * @param {Object} comment - Current comment object
 * @param {Object} likeData - Like data from API
 * @returns {Object} - Updated comment object
 */
export const updateCommentWithLikeData = (comment, likeData) => {
  try {
    if (!comment || !likeData) return comment;

    return {
      ...comment,
      likes: likeData.likeCount,
      liked: likeData.liked,
      userReaction: likeData.liked ? 'like' : null,
      reactions: {
        ...comment.reactions,
        like: likeData.likeCount,
      },
    };
  } catch (error) {
    console.error('Error updating comment with like data:', error);
    return comment;
  }
};

/**
 * Add new comment to comments list
 * Handles both top-level comments and nested replies
 * @param {Array} commentsList - Current comments list
 * @param {Object} newComment - New comment to add (in UI format)
 * @param {number} [parentId] - Parent comment ID if this is a reply
 * @returns {Array} - Updated comments list
 */
export const addCommentToList = (commentsList, newComment, parentId = null) => {
  try {
    if (!newComment) return commentsList;

   

    // If no parent, add as top-level comment AT THE END (below last comment)
    if (!parentId) {
      return [...commentsList, newComment]; // ✅ Append to end, not beginning
    }

    // Otherwise, find parent and add as reply (recursive)
    return commentsList.map((comment) => {
      if (String(comment.id) === String(parentId)) {
        return {
          ...comment,
          replies: [...(comment.replies || []), newComment], // Append to end
        };
      }

      // Recursively check nested replies
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: addCommentToList(comment.replies, newComment, parentId),
        };
      }

      return comment;
    });
  } catch (error) {
    console.error('Error adding comment to list:', error);
    return commentsList;
  }
};

/**
 * Update existing comment in list
 * Handles nested updates recursively
 * @param {Array} commentsList - Current comments list
 * @param {number|string} commentId - ID of comment to update
 * @param {Object|Function} updates - Updates to apply (object or function that receives current comment)
 * @returns {Array} - Updated comments list
 */
export const updateCommentInList = (commentsList, commentId, updates) => {
  try {
    if (!commentId || !updates) {
      console.warn('[updateCommentInList] Missing commentId or updates', { commentId, updates });
      return commentsList;
    }

    // Convert to string for comparison (handles both string and number IDs)
    const searchId = String(commentId);
    let found = false;

    const result = commentsList.map((comment) => {
      // Compare as strings to handle type mismatch
      if (String(comment.id) === searchId) {
        found = true;
      

        // Support function updates for accessing current state
        if (typeof updates === 'function') {
          const computedUpdates = updates(comment);
          const updated = { ...comment, ...computedUpdates };
         
          return updated;
        }
        const updated = { ...comment, ...updates };
      
        return updated;
      }

      // Recursively update in nested replies
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: updateCommentInList(comment.replies, commentId, updates),
        };
      }

      return comment;
    });

    if (!found) {
      console.warn('[updateCommentInList] Comment NOT FOUND in list!', {
        searchId,
        availableIds: commentsList.map(c => ({ id: c.id, type: typeof c.id })),
      });
    }

    return result;
  } catch (error) {
    console.error('[updateCommentInList] Error:', error);
    return commentsList;
  }
};

/**
 * Delete comment from list
 * Handles nested deletion recursively
 * @param {Array} commentsList - Current comments list
 * @param {number} commentId - ID of comment to delete
 * @returns {Array} - Updated comments list
 */
export const deleteCommentFromList = (commentsList, commentId) => {
  try {
    if (!commentId) return commentsList;

    return commentsList
      .filter((comment) => comment.id !== commentId)
      .map((comment) => {
        // Recursively delete from nested replies
        if (comment.replies && comment.replies.length > 0) {
          return {
            ...comment,
            replies: deleteCommentFromList(comment.replies, commentId),
          };
        }
        return comment;
      });
  } catch (error) {
    console.error('Error deleting comment from list:', error);
    return commentsList;
  }
};

/**
 * Toggle comment like status in list
 * Updates like count and liked status recursively
 * @param {Array} commentsList - Current comments list
 * @param {number} commentId - ID of comment to toggle like
 * @param {Object} likeData - Like data from API
 * @returns {Array} - Updated comments list
 */
export const toggleCommentLikeInList = (commentsList, commentId, likeData) => {
  try {
    if (!commentId || !likeData) return commentsList;

    return commentsList.map((comment) => {
      // If this is the comment to update
      if (comment.id === commentId) {
        return updateCommentWithLikeData(comment, likeData);
      }

      // Recursively update in nested replies
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: toggleCommentLikeInList(comment.replies, commentId, likeData),
        };
      }

      return comment;
    });
  } catch (error) {
    console.error('Error toggling comment like in list:', error);
    return commentsList;
  }
};

/**
 * Map new comment API response to UI format
 * Used when creating a new comment/reply
 * Backend doesn't return user data, so we accept it as parameters
 * @param {Object} apiResponse - Response from add comment endpoint
 * @param {Object} userData - User data { name, image } from Redux
 * @returns {Object} - Transformed comment for UI
 */
export const mapNewCommentFromAPI = (apiResponse, userData = null) => {
  try {
    // Handle nested response structure: response.data.data.comment
    const comment = apiResponse?.data?.data?.comment || apiResponse?.data?.comment;

    if (!comment) {
      console.warn('[mapNewCommentFromAPI] No comment in response:', apiResponse);
      return null;
    }

    const apiComment = comment;

   
    // Use provided user data or fallback to placeholder
    const author = userData?.name || 'You';
    // const authorImage = userData?.image || generateAvatarUrl(author);
    const authorImage = userData?.image;


    return {
      id: apiComment.id,
      author: author,
      authorImage: authorImage,
      content: apiComment.content || '',
      image: (() => {
        const imageValue = apiComment.image_url;
        if (!imageValue) return null;
        
        // If it's a blob URL, return as-is (for optimistic updates)
        if (imageValue.startsWith('blob:')) {
          return imageValue;
        }
        
        // If it already contains the full path with base URL, return as-is
        if (imageValue.includes('/uploads/comment_image/')) {
          // Check if it already has the protocol (http/https)
          if (imageValue.startsWith('http')) {
            return imageValue;
          }
          // If it starts with /uploads, prepend only the base URL
          if (imageValue.startsWith('/uploads')) {
            return `${process.env.REACT_APP_API_BASE_URL}${imageValue}`;
          }
          // If it already has the path but no protocol, it might be malformed
          return imageValue;
        }
        
        // Otherwise, it's just a filename - construct full URL
        return `${process.env.REACT_APP_API_BASE_URL}/uploads/comment_image/${imageValue}`;
      })(),
      time: 'Just now',
      link_preview: apiComment.link_preview || null,
      likes: 0,
      liked: false,
      userReaction: null,
      reactions: {
        like: 0,
        love: 0,
        haha: 0,
        wow: 0,
        sad: 0,
        angry: 0,
      },
      replies: [],
      parentId: apiComment.parent_id || null,
      isEditable: true,
      isEditing: false,
      _optimistic: false, // Real comment from server
      _metadata: {
        postId: apiComment.post_id,
        userId: apiComment.user_id, // ✅ User ID from backend
        createdAt: apiComment.created_at,
        updatedAt: apiComment.updated_at,
      },
    };
  } catch (error) {
    console.error('[mapNewCommentFromAPI] Error:', error, apiResponse);
    return null;
  }
};
